import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import AuthShell, { GoogleButton } from "@/pages/AuthShell";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("error") === "google") toast.error("Google sign-in failed. Please try again.");
  }, [params]);

  useEffect(() => {
    if (user && typeof user === "object") navigate(user.role === "admin" ? "/admin" : next, { replace: true });
  }, [user, navigate, next]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : next, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Login to Your Account"
      subtitle="Access all free Revision Kits and your dashboard"
      footer={<>New here? <Link to={`/register${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="prose-link font-semibold" data-testid="link-register">Create a free account</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="mb-1 block text-sm font-semibold text-[#0B2545]">Email</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68]" data-testid="login-email" />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-sm font-semibold text-[#0B2545]">Password</label>
            <Link to="/forgot-password" className="prose-link text-xs" data-testid="link-forgot">Forgot password?</Link>
          </div>
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68]" data-testid="login-password" />
        </div>
        {error && <p className="rounded-sm bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#1F3A68] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B2545] disabled:opacity-60" data-testid="login-submit">
          {loading && <Loader2 size={16} className="animate-spin" />} Login
        </button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-gray-400"><div className="h-px flex-1 bg-gray-200" /> OR <div className="h-px flex-1 bg-gray-200" /></div>
      <GoogleButton next={next} testId="login-google" />
    </AuthShell>
  );
}
